import { useCallback, useEffect, useMemo, useState } from 'react'
import { NetworkStatus, useQuery } from '@apollo/client'
import { MY_PGA_HOPE_GRADUATE_CARDS } from './queries'
import { flattenGraduateCards } from './utils'

// `errorPolicy: 'all'` keeps a backend that has not shipped the field yet from
// turning into a blank screen; with no cards the entry point renders nothing.
export const useMyPgaHopeGraduateCards = ({ skip = false } = {}) => {
  const result = useQuery(MY_PGA_HOPE_GRADUATE_CARDS, {
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })
  const { refetch: apolloRefetch } = result
  const cards = useMemo(() => flattenGraduateCards(result.data), [result.data])

  // Set only when a fresh network result lands, never for cache reads, so the
  // card's "Verified" time reflects a real round trip.
  const [fetchedAt, setFetchedAt] = useState(null)
  const { error, networkStatus } = result
  useEffect(() => {
    if (networkStatus === NetworkStatus.ready && result.data && !error) {
      setFetchedAt(new Date())
    }
  }, [result.data, error, networkStatus])

  // A refetch that returns identical data would not re-run the effect above,
  // so a successful one stamps the time itself. A failed one keeps the cards
  // and the previous time, which the card then shows as "Last verified".
  const refetch = useCallback(async () => {
    try {
      const refetched = await apolloRefetch()
      if (refetched?.data && !refetched.error && !refetched.errors?.length) {
        setFetchedAt(new Date())
      }
    } catch (e) {
      // The query's own `error` already reflects the failure.
    }
  }, [apolloRefetch])

  return { ...result, cards, fetchedAt, refetch }
}
